import React, { useState } from 'react';
import { FormItemWrap } from '@unicus/react-ui-kit';
import { Form, Input, Row, Col, Checkbox, Button, Modal, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import useSegmentField from 'core/hooks/useSegmentField';
import {
  closeAnnotation,
  createAnnotation
} from 'stores/patients/patientActions';
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import RemoteSelectAutoCrud from 'components/RemoteSelectAutoCrud';
import isNullOrEmpty from 'util/functions/isNullOrEmpty';
import { normalizeFromSelect } from 'util/converters';

const colors = [
  '#00d7ff',
  '#ff7963',
  '#847770',
  '#73bafe',
  '#004ab0',
  '#efd066',
  '#e885a5'
];

const getRandomColor = () => {
  const index = Math.floor(Math.random() * colors.length);
  return colors[index];
};

const PatientAnnotationModalWrapper = ({ patient, loading }) => {
  const [changed, setChanged] = useState(false);
  const { treatment } = patient ?? {};
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const segmentFields = useSegmentField();

  if (isNullOrEmpty(patient, 'object')) {
    return null;
  }

  const handleChangeValues = (fields, _) => {
    if (!changed) {
      setChanged(true);
    }

    if (fields.shared) {
      Modal.confirm({
        title: `Deseja compartilhar essa anotação com o ${segmentFields.serviceRecipient}?`,
        content: `Anotações compartilhadas ficarão visíveis ao ${segmentFields.serviceRecipient}.`,
        cancelText: 'Não compartilhar',
        okText: 'Compartilhar',
        width: 600,
        onCancel: () => {
          form.setFieldsValue({ shared: false });
        }
      });
    }
  };

  const handleSuccess = () => {
    form.resetFields();
  };

  const handleSubmit = () => {
    form.validateFields().then(({ isLinkTreatment, markers, ...rest }) => {
      const values = { ...rest };

      if (isLinkTreatment) {
        values.treatment = treatment;
      } else {
        values.treatment = null;
      }

      values.markers = markers.map((c) => ({ marker: normalizeFromSelect(c) }));

      dispatch(createAnnotation(patient?.id, values, handleSuccess));
    });
  };

  const tagRender = (props, item) => {
    const { label, onClose } = props;

    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <div className="ant-select-selection-overflow-item">
        <span
          className="ant-select-selection-item"
          style={{ background: item?.color }}
        >
          <span className="ant-select-selection-item-content gx-text-white">
            {label}
          </span>
          <span className="ant-select-selection-item-remove">
            <CloseOutlined
              className="gx-text-white"
              onMouseDown={onPreventMouseDown}
              onClick={onClose}
            />
          </span>
        </span>
      </div>
    );
  };

  const handleCancel = () => {
    if (changed) {
      Modal.confirm({
        title: 'Você tem certeza que deseja sair sem salvar?',
        icon: <ExclamationCircleOutlined />,
        content: 'As alterações realizadas não foram salvas.',
        okText: 'Sair',
        cancelText: 'Cancelar',
        onOk() {
          dispatch(closeAnnotation());
        }
      });
    } else {
      dispatch(closeAnnotation());
    }
  };

  return (
    <Modal
      visible
      onCancel={handleCancel}
      width={800}
      maskClosable={false}
      confirmLoading={loading}
      okText="Salvar"
      onOk={handleSubmit}
      title="Anotações"
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ markers: [] }}
        onValuesChange={handleChangeValues}
        onBlur={() => {}}
      >
        <FormItemWrap name="annotation" required whitespace>
          <Input.TextArea
            placeholder="Faça aqui suas anotações"
            rows={4}
            autoFocus
          />
        </FormItemWrap>
        {segmentFields.isHealthcareEnabled && (
          <>
            <FormItemWrap name="markers">
              <RemoteSelectAutoCrud
                resource="markers"
                mode="multiple"
                placeholder="Etiquetas"
                onBeforeCreate={(name) => ({ name, color: getRandomColor() })}
                tagRender={tagRender}
                optionRender={(c) => (
                  <Select.Option key={c.key} value={c.color} label={c.label}>
                    {c.label}
                  </Select.Option>
                )}
              />
            </FormItemWrap>

            <Row align="middle" justify="space-between">
              <Col>
                {patient?.userId && (
                  <Form.Item
                    name="shared"
                    noStyle
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Checkbox>
                      Compartilhar com {segmentFields.serviceRecipient}
                    </Checkbox>
                  </Form.Item>
                )}
                {treatment ? (
                  <Form.Item
                    name="isLinkTreatment"
                    noStyle
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Checkbox>Adicionar ao tratamento</Checkbox>
                  </Form.Item>
                ) : null}
              </Col>
            </Row>
          </>
        )}
      </Form>
    </Modal>
  );
};

const PatientAnnotationModal = (props) => {
  const { createAnnotationData } = useSelector(
    (c) => c.resources?.patients ?? {}
  );

  if (!createAnnotationData) {
    return null;
  }

  return <PatientAnnotationModalWrapper {...createAnnotationData} {...props} />;
};

export default PatientAnnotationModal;


